import {FormControl, FormField, Option} from 'platform/components';
import {VStack, Space, Grid, GridItem} from 'platform/foundation';
import {Nullish} from 'utility-types';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil} from 'ramda';

import {i18n} from '@dms/i18n';
import {
  DocumentTemplateOptionGroup,
  SettingsFooter,
  SettingsSection,
  getOrderPaymentTypeOptions,
} from '@dms/settings/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {WarehouseDirectSaleVariantsDetailFormSchemaType} from '../../types/WarehouseDirectSaleVariantsDetailFormSchema';
import {FreeOfChargeCard} from './components/FreeOfChargeCard';
import {PaymentCard} from './components/PaymentCard';

interface PaymentTabProps extends RequiredTestIdProps {
  control: FormControl<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  formApi: UseFormReturn<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  onDiscard: VoidFunction;
  directSaleVariantId: string | Nullish;
  standardInvoiceTemplateOptions: DocumentTemplateOptionGroup[];
  internalInvoiceTemplateOptions: DocumentTemplateOptionGroup[];
  invoiceDocSeriesOptions: Option[];
}

export function PaymentTab(props: PaymentTabProps) {
  const documents = useWatch({control: props.control, name: 'documents'});
  const paymentTypeOptions = getOrderPaymentTypeOptions(documents);

  return (
    <>
      <SettingsSection>
        <VStack spacing={4}>
          <Grid columns={2}>
            <GridItem span={1}>
              <FormField
                control={props.control}
                name="documents.defaultPaymentType"
                type="choice"
                label={i18n.t('entity.warehouse.labels.defaultPaymentMethod')}
                options={paymentTypeOptions}
                isDisabled={!paymentTypeOptions.length}
                data-testid={suffixTestId('offerSeriesId', props)}
              />
            </GridItem>
          </Grid>

          <PaymentCard
            control={props.control}
            formApi={props.formApi}
            path="cardPayment"
            paymentType="CARD"
            title={i18n.t('entity.warehouse.labels.cardPayment')}
            invoiceTemplateOptions={props.standardInvoiceTemplateOptions}
            invoiceDocSeriesOptions={props.invoiceDocSeriesOptions}
            data-testid={suffixTestId('paymentType.card', props)}
          />

          <PaymentCard
            control={props.control}
            formApi={props.formApi}
            path="cashPayment"
            paymentType="CASH"
            title={i18n.t('entity.warehouse.labels.cashPayment')}
            invoiceTemplateOptions={props.standardInvoiceTemplateOptions}
            invoiceDocSeriesOptions={props.invoiceDocSeriesOptions}
            data-testid={suffixTestId('paymentType.cash', props)}
          />

          <PaymentCard
            control={props.control}
            formApi={props.formApi}
            path="cashOnDeliveryPayment"
            paymentType="CASH_ON_DELIVERY"
            title={i18n.t('entity.invoice.paymentMethod.cashOnDelivery')}
            invoiceTemplateOptions={props.standardInvoiceTemplateOptions}
            invoiceDocSeriesOptions={props.invoiceDocSeriesOptions}
            data-testid={suffixTestId('paymentType.cashOnDelivery', props)}
          />

          <PaymentCard
            control={props.control}
            formApi={props.formApi}
            path="bankPayment"
            paymentType="BANK_TRANSFER"
            title={i18n.t('entity.warehouse.labels.bankPayment')}
            invoiceTemplateOptions={props.standardInvoiceTemplateOptions}
            invoiceDocSeriesOptions={props.invoiceDocSeriesOptions}
            data-testid={suffixTestId('paymentType.bankTransfer', props)}
          />
          <FreeOfChargeCard
            control={props.control}
            formApi={props.formApi}
            invoiceTemplateOptions={props.internalInvoiceTemplateOptions}
            invoiceDocSeriesOptions={props.invoiceDocSeriesOptions}
            data-testid={suffixTestId('paymentType.internal', props)}
          />
        </VStack>
      </SettingsSection>

      <Space vertical={18} />

      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.discard')
              : i18n.t('general.actions.discardChanges'),
            onClick: props.onDiscard,
            'data-testid': suffixTestId('discard', props),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.save')
              : i18n.t('general.actions.saveChanges'),
            'data-testid': suffixTestId('submit', props),
          },
        ]}
      />
    </>
  );
}
